import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Shield, Calendar, AlertTriangle, CheckCircle2, Clock, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GovernanceDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [policies, setPolicies] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState(null);

  // Premium Dark Teal-Green Gradient Palette (same as HomePage)
  const colors = {
    sageTeal: '#97B6A7',
    softTeal: '#759B89',
    mutedTeal: '#587F6C',
    forestTeal: '#0E1E19',
    accentGold: '#D4B896',
    accentCopper: '#C9A77D',
    textLight: '#F7F9F8',
    textMuted: '#B8C9C0',
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('xchange_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [policiesRes, calendarRes, alertsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/governance/policies`, config),
        axios.get(`${API}/governance/calendar`, config),
        axios.get(`${API}/governance/risk-alerts`, config),
        axios.get(`${API}/governance/summary`, config)
      ]);

      setPolicies(policiesRes.data);
      setCalendar(calendarRes.data);
      setAlerts(alertsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to load governance data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.forestTeal }}>
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: colors.accentGold, borderTopColor: 'transparent' }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.forestTeal }}>
      <Header />

      {/* Hero Section - Same gradient as HomePage */}
      <section 
        className="relative pt-24 pb-12 px-4 overflow-hidden"
        style={{ 
          background: `linear-gradient(135deg, ${colors.sageTeal} 0%, ${colors.softTeal} 35%, ${colors.mutedTeal} 70%, ${colors.forestTeal} 100%)`
        }}
      >
        <div 
          className="absolute inset-0 opacity-5"
          style={{ 
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.3) 1px, transparent 0)',
            backgroundSize: '32px 32px'
          }}
        />
        <div className="max-w-7xl mx-auto relative z-10">
          <h1 className="text-3xl lg:text-4xl font-bold mb-2" style={{ color: colors.textLight }}>Governance & Compliance Suite</h1>
          <p className="text-lg" style={{ color: colors.textMuted }}>Automated compliance management and risk monitoring</p>
        </div>
      </section>

      <div className="px-4 sm:px-6 lg:px-8 py-8" style={{ backgroundColor: colors.mutedTeal }}>
        <div className="max-w-7xl mx-auto">
          {/* Summary Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div 
              data-testid="total-policies-card" 
              className="p-6 rounded-xl transition-all duration-300"
              style={{ 
                backgroundColor: 'rgba(247, 249, 248, 0.95)', 
                border: '1px solid rgba(151, 182, 167, 0.3)',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm" style={{ color: colors.mutedTeal }}>Total Policies</span>
                <Shield style={{ color: colors.accentGold }} size={20} />
              </div>
              <div className="text-3xl font-bold" style={{ color: colors.forestTeal }}>{summary?.total_policies || 0}</div>
              <div className="text-sm mt-1" style={{ color: colors.mutedTeal }}>Under monitoring</div>
              <div className="h-1 w-12 rounded-full mt-3" style={{ backgroundColor: colors.accentGold }} />
            </div>

            <div 
              data-testid="compliance-rate-card" 
              className="p-6 rounded-xl transition-all duration-300"
              style={{ 
                backgroundColor: 'rgba(247, 249, 248, 0.95)', 
                border: '1px solid rgba(151, 182, 167, 0.3)',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm" style={{ color: colors.mutedTeal }}>Compliance Rate</span>
                <CheckCircle2 style={{ color: colors.accentGold }} size={20} />
              </div>
              <div className="text-3xl font-bold" style={{ color: colors.forestTeal }}>{summary?.compliance_rate?.toFixed(1) || 0}%</div>
              <div className="text-sm mt-1" style={{ color: colors.accentGold }}>Target: 95%</div>
              <div className="h-1 w-12 rounded-full mt-3" style={{ backgroundColor: colors.accentGold }} />
            </div>

            <div 
              data-testid="upcoming-tasks-card" 
              className="p-6 rounded-xl transition-all duration-300"
              style={{ 
                backgroundColor: 'rgba(247, 249, 248, 0.95)', 
                border: '1px solid rgba(151, 182, 167, 0.3)',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm" style={{ color: colors.mutedTeal }}>Upcoming Tasks</span>
                <Calendar style={{ color: colors.accentGold }} size={20} />
              </div>
              <div className="text-3xl font-bold" style={{ color: colors.forestTeal }}>{summary?.upcoming_tasks || 0}</div>
              <div className="text-sm mt-1" style={{ color: colors.mutedTeal }}>Next 30 days</div>
              <div className="h-1 w-12 rounded-full mt-3" style={{ backgroundColor: colors.accentGold }} />
            </div>

            <div 
              data-testid="active-alerts-card" 
              className="p-6 rounded-xl transition-all duration-300"
              style={{ 
                backgroundColor: 'rgba(247, 249, 248, 0.95)', 
                border: '1px solid rgba(151, 182, 167, 0.3)',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm" style={{ color: colors.mutedTeal }}>Active Alerts</span>
                <AlertTriangle style={{ color: colors.accentGold }} size={20} />
              </div>
              <div className="text-3xl font-bold" style={{ color: colors.forestTeal }}>{summary?.active_alerts || 0}</div>
              <div className="text-sm mt-1" style={{ color: colors.mutedTeal }}>Requires attention</div>
              <div className="h-1 w-12 rounded-full mt-3" style={{ backgroundColor: colors.accentGold }} />
            </div>
          </div>

          {/* Policy Compliance and Risk Alerts */}
          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            <div 
              data-testid="policy-list" 
              className="p-6 rounded-xl"
              style={{ 
                backgroundColor: 'rgba(14, 30, 25, 0.4)', 
                border: '1px solid rgba(151, 182, 167, 0.25)',
                backdropFilter: 'blur(8px)'
              }}
            >
              <h3 className="text-xl font-semibold mb-4" style={{ color: colors.textLight }}>Policy Compliance Tracker</h3>
              <div className="space-y-3">
                {policies.slice(0, 6).map((policy) => (
                  <div 
                    key={policy.id} 
                    data-testid={`policy-${policy.id}`} 
                    className="flex items-center justify-between p-3 rounded-lg"
                    style={{ backgroundColor: 'rgba(14, 30, 25, 0.3)' }}
                  >
                    <div className="flex-1">
                      <div className="font-medium" style={{ color: colors.textLight }}>{policy.name}</div>
                      <div className="text-sm" style={{ color: colors.textMuted }}>{policy.framework}</div>
                    </div>
                    <div 
                      className="px-3 py-1 rounded-full text-xs font-semibold"
                      style={{ 
                        backgroundColor: policy.status === 'Compliant' ? colors.sageTeal : policy.status === 'Pending Review' ? colors.softTeal : colors.mutedTeal,
                        color: colors.forestTeal
                      }}
                    >
                      {policy.status}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Risk Alerts */}
            <div 
              data-testid="risk-alerts" 
              className="p-6 rounded-xl"
              style={{ 
                backgroundColor: 'rgba(14, 30, 25, 0.4)', 
                border: '1px solid rgba(151, 182, 167, 0.25)',
                backdropFilter: 'blur(8px)'
              }}
            >
              <h3 className="text-xl font-semibold mb-4" style={{ color: colors.textLight }}>AI-Driven Risk Alerts</h3>
              <div className="space-y-3">
                {alerts.map((alert) => (
                  <div
                    key={alert.id}
                    data-testid={`alert-${alert.id}`}
                    className="p-4 rounded-lg"
                    style={{
                      backgroundColor: 'rgba(14, 30, 25, 0.3)',
                      borderLeft: `4px solid ${alert.severity === 'high' ? colors.accentGold : alert.severity === 'medium' ? colors.softTeal : colors.mutedTeal}`
                    }}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center">
                        <AlertTriangle
                          size={18}
                          style={{ color: alert.severity === 'high' ? colors.accentGold : colors.textMuted }}
                          className="mr-2"
                        />
                        <span className="font-medium" style={{ color: colors.textLight }}>{alert.title}</span>
                      </div>
                      <span 
                        className="text-xs px-2 py-1 rounded"
                        style={{ backgroundColor: 'rgba(151, 182, 167, 0.2)', color: colors.textMuted }}
                      >
                        {alert.severity.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm mb-2" style={{ color: colors.textMuted }}>{alert.description}</p>
                    <div className="text-xs" style={{ color: colors.textMuted }}>Detected: {new Date(alert.detected_at).toLocaleDateString()}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Governance Calendar */}
          <div 
            data-testid="governance-calendar" 
            className="p-6 rounded-xl mb-8"
            style={{ 
              backgroundColor: 'rgba(14, 30, 25, 0.4)', 
              border: '1px solid rgba(151, 182, 167, 0.25)',
              backdropFilter: 'blur(8px)'
            }}
          >
            <h3 className="text-xl font-semibold mb-4" style={{ color: colors.textLight }}>Governance Calendar</h3>
            <div className="space-y-3">
              {calendar.map((event) => (
                <div 
                  key={event.id} 
                  data-testid={`calendar-event-${event.id}`} 
                  className="flex items-start space-x-4 p-4 rounded-lg"
                  style={{ backgroundColor: 'rgba(14, 30, 25, 0.3)' }}
                >
                  <div className="flex-shrink-0 w-16 text-center">
                    <div className="text-2xl font-bold" style={{ color: colors.accentGold }}>
                      {new Date(event.due_date).getDate()}
                    </div>
                    <div className="text-xs uppercase" style={{ color: colors.textMuted }}>
                      {new Date(event.due_date).toLocaleDateString('en-US', { month: 'short' })}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center mb-1">
                      <Calendar size={16} style={{ color: colors.accentGold }} className="mr-2" />
                      <span className="font-medium" style={{ color: colors.textLight }}>{event.title}</span>
                    </div>
                    <p className="text-sm mb-2" style={{ color: colors.textMuted }}>{event.description}</p>
                    <div className="flex items-center text-xs">
                      <Clock size={14} style={{ color: colors.textMuted }} className="mr-1" />
                      <span style={{ color: colors.textMuted }}>
                        {Math.ceil((new Date(event.due_date) - new Date()) / (1000 * 60 * 60 * 24))} days remaining
                      </span>
                    </div>
                  </div>
                  <div>
                    <span 
                      className="px-3 py-1 rounded-full text-xs font-semibold"
                      style={{ 
                        backgroundColor: event.priority === 'high' ? colors.accentGold : event.priority === 'medium' ? colors.softTeal : colors.mutedTeal,
                        color: colors.forestTeal
                      }}
                    >
                      {event.priority.toUpperCase()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Document Repository */}
          <div 
            data-testid="document-repository" 
            className="p-6 rounded-xl"
            style={{ 
              backgroundColor: 'rgba(14, 30, 25, 0.4)', 
              border: '1px solid rgba(151, 182, 167, 0.25)',
              backdropFilter: 'blur(8px)'
            }}
          >
            <h3 className="text-xl font-semibold mb-4" style={{ color: colors.textLight }}>Digital Document Repository</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { name: 'CSR Policy 2024', type: 'PDF', size: '2.3 MB', date: '2024-01-15' },
                { name: 'BRSR Report', type: 'PDF', size: '4.1 MB', date: '2024-02-20' },
                { name: 'Board Meeting Minutes', type: 'DOCX', size: '890 KB', date: '2024-03-10' },
                { name: 'Audit Report Q1', type: 'PDF', size: '3.2 MB', date: '2024-04-05' },
                { name: 'Compliance Checklist', type: 'XLSX', size: '1.1 MB', date: '2024-04-18' },
                { name: 'Risk Assessment', type: 'PDF', size: '2.8 MB', date: '2024-05-01' }
              ].map((doc, index) => (
                <div 
                  key={index} 
                  className="p-4 rounded-lg transition-all duration-300"
                  style={{ 
                    backgroundColor: 'rgba(14, 30, 25, 0.3)',
                    border: '1px solid rgba(151, 182, 167, 0.2)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = colors.accentGold;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(151, 182, 167, 0.2)';
                  }}
                >
                  <div className="flex items-start justify-between mb-2">
                    <FileText style={{ color: colors.accentGold }} size={24} />
                    <span 
                      className="text-xs px-2 py-1 rounded"
                      style={{ backgroundColor: 'rgba(151, 182, 167, 0.2)', color: colors.textMuted }}
                    >
                      {doc.type}
                    </span>
                  </div>
                  <div className="font-medium mb-1" style={{ color: colors.textLight }}>{doc.name}</div>
                  <div className="text-xs" style={{ color: colors.textMuted }}>{doc.size} • {new Date(doc.date).toLocaleDateString()}</div>
                  <button 
                    className="w-full mt-3 py-2 rounded-lg text-xs font-medium transition-all"
                    style={{ 
                      backgroundColor: 'transparent', 
                      color: colors.accentGold, 
                      border: `1px solid ${colors.accentGold}` 
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(212, 184, 150, 0.15)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    View Document
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default GovernanceDashboard;
